<template>
    <b-overlay :show="loading" rounded="sm" class="h-100">
        <div class="card mb-0 h-100">
            <div class="card-body p-0">
                <ErrorMessage :value="errorMessage" class="m-3" />
                <div class="media p-3 gap-3" :class="{'d-flex flex-column': !data}">
                    <div class="media-body">
                        <span class="text-muted text-uppercase font-size-12 font-weight-bold">
                            <Tooltip v-if="titleTooltip" :text="titleTooltip" :width="titleTooltipWidth" type="info" placement="top">{{ title }}</Tooltip>
                            <span v-else>{{ title }}</span>
                        </span>
                        <h2 class="mb-0 nowrap">
                            {{ value }}
                        </h2>
                    </div>
                    <div v-if="showChart || showDifference" :class="{'align-self-center': !!data}">
                        <EChart
                            v-if="showChart"
                            :options="eChartOptions"
                            :height="45"
                            :width="90"
                        />
                        <span v-if="showDifference" :class="{ 'text-danger': `${parseFloat(difference)}` < 0 }" class="text-success font-weight-bold font-size-13">
                            <feather
                                v-if="parseFloat(difference) !== 0"
                                :type="parseFloat(difference) > 0 ? 'arrow-up' : 'arrow-down'"
                                size="12px"
                                stroke-width="3"
                            />
                            {{ difference }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </b-overlay>
</template>



<script>
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import EChart from '@/components/common/EChart.vue';
    import Tooltip from '@/components/common/Tooltip.vue';

    export default {
        name: 'WidgetStatChart',
        components: {
            ErrorMessage,
            EChart,
            Tooltip,
        },
        props: {
            title: {
                type: String,
                default: '',
            },
            value: {
                type: [Number, String],
                default: 0,
            },
            difference: {
                type: [Number, String],
                default: null,
            },
            chartColor: {
                type: String,
                default: '#5369f8',
            },
            data: {
                type: Array,
                default: null,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            error: {
                type: [Error, Object, String],
                default: null,
            },
            tooltipFormatter: {
                type: [Function, String],
                default: null,
            },
            titleTooltip: {
                type: String,
                default: null,
            },
            titleTooltipWidth: {
                type: Number,
                default: 300,
            },
        },
        data() {
            return {};
        },
        computed: {
            errorMessage() {
                return this.error?.message || this.error || null;
            },
            showChart() {
                return Array.isArray(this.data);
            },
            showDifference() {
                return this.difference !== null && this.difference !== undefined;
            },
            eChartOptions() {
                const hasData = Array.isArray(this.data) && this.data.length > 0;

                // Normalizacja: [n] | [{x,y}] | [[x,y]] -> [y]
                const toY = item => (Array.isArray(item) ? item[1] : item?.y ?? item?.value ?? item ?? 0);
                const seriesData = hasData ? this.data.map(toY) : [0, 0];

                const tooltipFormatter = params => {
                    const data = Array.isArray(params) ? params[0] : params;
                    const value = data?.value ?? '';
                    let formattedValue = value;
                    if (this.tooltipFormatter === 'round') formattedValue = Math.round(value);
                    if (this.tooltipFormatter === 'currency') formattedValue = this.$format.currency(value);
                    if (this.tooltipFormatter === 'percent') formattedValue = this.$format.percent(value);
                    if (typeof this.tooltipFormatter === 'function') formattedValue = this.tooltipFormatter(value);
                    return formattedValue.toString();
                };

                return {
                    legend: { show: false },
                    grid: { left: 0, right: 0, top: 3, bottom: 3, containLabel: false },
                    xAxis: { show: false, type: 'category', boundaryGap: false },
                    yAxis: { show: false, type: 'value', scale: true },
                    tooltip: { trigger: 'axis', axisPointer: { type: 'line' }, formatter: tooltipFormatter },
                    series: [
                        {
                            type: 'line',
                            data: seriesData,
                            lineStyle: { width: 2, color: this.chartColor },
                            areaStyle: { opacity: 0.3 },
                        },
                    ],
                };
            },
        },
    };
</script>



<style lang="scss" scoped>
    ::v-deep {
        .tooltip-wrapper {
            i.feather {
                color: $body-color;
            }
        }
    }
</style>
